import {
  AdministrativeSectionKey,
  getAppConfig,
  isAdministrativeSectionEnabled
} from '../config/app.config';

export interface AdministrativeMember {
  fullName: string;
  memberNumber: string;
  profession: string;
  licenseNumber: string;
  status: string;
  validUntil: string;
  email: string;
  phone: string;
  company: string;
  position: string;
}

export interface AdministrativeEventSession {
  id: string;
  time: string;
  type: string;
  title: string;
  detail: string;
}

export interface AdministrativeEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  format: string;
  status: 'upcoming' | 'history';
  confirmed: boolean;
  sessions: AdministrativeEventSession[];
}

export interface AdministrativeParticipation {
  id: string;
  type: 'vote' | 'survey' | 'call';
  label: string;
  title: string;
  description: string;
  deadline: string;
  progress?: number;
  options?: string[];
}

export interface AdministrativeMovement {
  id: string;
  title: string;
  detail: string;
  amount: number;
  status: 'paid' | 'pending';
}

export interface AdministrativeBenefit {
  id: string;
  name: string;
  category: 'salud' | 'calzado' | 'educacion' | 'gastronomia' | 'bienestar' | 'servicios';
  offer: string;
  summary: string;
  description: string;
  terms: string;
}

export interface AdministrativeNews {
  id: string;
  category: string;
  title: string;
  summary: string;
  publishedAt: string;
  featured?: boolean;
}

export interface AdministrativeCertificate {
  id: string;
  title: string;
  detail: string;
  available: boolean;
}

export interface AdministrativeSectionDataMap {
  membership: { member: AdministrativeMember };
  associationEvents: { events: AdministrativeEvent[] };
  participation: { items: AdministrativeParticipation[] };
  accountStatement: {
    currency: string;
    balance: number;
    nextDueDate: string;
    reminderEnabled: boolean;
    movements: AdministrativeMovement[];
  };
  digitalCard: { member: AdministrativeMember };
  benefits: { items: AdministrativeBenefit[] };
  news: { items: AdministrativeNews[]; unread: number };
  certificates: { items: AdministrativeCertificate[] };
}

const member: AdministrativeMember = {
  fullName: 'María Fernanda López',
  memberNumber: '00842',
  profession: 'Farmacéutica',
  licenseNumber: 'F-18427',
  status: 'Miembro activo',
  validUntil: '31 dic. 2026',
  email: 'maria.fernanda@example.com',
  phone: '+507 6000-8420',
  company: 'Farmacia Central',
  position: 'Regente farmacéutica'
};

const dummySections: AdministrativeSectionDataMap = {
  membership: { member },
  associationEvents: {
    events: [
      {
        id: 'jornada-2026',
        title: 'Jornada Nacional de Actualización Farmacéutica 2026',
        date: '18 de agosto de 2026',
        time: '8:00 a. m.',
        location: 'Hotel Panamá · Salón Gran Panamá',
        format: 'Presencial',
        status: 'upcoming',
        confirmed: true,
        sessions: [
          { id: 'session-1', time: '08:00', type: 'APERTURA', title: 'Bienvenida e inauguración', detail: 'Salón Principal' },
          { id: 'session-2', time: '09:00', type: 'CONFERENCIA', title: 'Innovación y seguridad en la práctica farmacéutica', detail: 'Dra. Laura Méndez · Sala A' },
          { id: 'session-3', time: '10:30', type: 'PANEL', title: 'Calidad, trazabilidad y atención farmacéutica', detail: 'Panel de especialistas · Sala A' }
        ]
      },
      {
        id: 'workshop-2026',
        title: 'Taller de buenas prácticas de dispensación',
        date: '4 de septiembre de 2026',
        time: '3:00 p. m.',
        location: 'Sesión en línea',
        format: 'Virtual',
        status: 'upcoming',
        confirmed: false,
        sessions: []
      },
      {
        id: 'quality-2026',
        title: 'Seminario de Control de Calidad',
        date: '15 de marzo de 2026',
        time: '9:00 a. m.',
        location: 'Auditorio institucional',
        format: 'Presencial',
        status: 'history',
        confirmed: true,
        sessions: []
      }
    ]
  },
  participation: {
    items: [
      {
        id: 'board-2027',
        type: 'vote',
        label: 'VOTACIÓN ABIERTA',
        title: 'Elección de Junta Directiva 2027–2029',
        description: 'Tu membresía está habilitada para votar.',
        deadline: 'Cierra hoy a las 6:00 p. m.',
        progress: 68,
        options: ['Nómina Integración', 'Nómina Evolución']
      },
      {
        id: 'education-2027',
        type: 'survey',
        label: 'ENCUESTA',
        title: 'Prioridades de educación continua 2027',
        description: 'Ayúdanos a preparar el próximo calendario científico.',
        deadline: 'Duración estimada: 2 minutos'
      },
      {
        id: 'assembly-2026',
        type: 'call',
        label: 'CONVOCATORIA',
        title: 'Asamblea General Ordinaria',
        description: '25 de agosto · 5:30 p. m. · Modalidad híbrida',
        deadline: 'Confirmación disponible'
      }
    ]
  },
  accountStatement: {
    currency: 'B/.',
    balance: 75,
    nextDueDate: '5 de agosto',
    reminderEnabled: true,
    movements: [
      { id: 'jul-2026', title: 'Cuota julio 2026', detail: 'Pago en línea · 05 jul.', amount: 75, status: 'paid' },
      { id: 'event-2026', title: 'Jornada institucional 2026', detail: 'Inscripción · 18 jun.', amount: 120, status: 'paid' },
      { id: 'aug-2026', title: 'Cuota agosto 2026', detail: 'Vence el 05 ago.', amount: 75, status: 'pending' }
    ]
  },
  digitalCard: { member },
  benefits: {
    items: [
      { id: 'clinic', name: 'Clínica Vida', category: 'salud', offer: '20% dto.', summary: 'Consultas y especialidades', description: 'Consultas médicas generales y especialidades seleccionadas.', terms: 'Presentar el carné digital vigente. Cita previa requerida.' },
      { id: 'lab', name: 'Laboratorios Nova', category: 'salud', offer: '15% dto.', summary: 'Pruebas y perfiles clínicos', description: 'Pruebas de laboratorio y perfiles preventivos.', terms: 'No acumulable con promociones.' },
      { id: 'pharmacy', name: 'Farma Cerca', category: 'salud', offer: '12% dto.', summary: 'Farmacia y bienestar', description: 'Productos seleccionados.', terms: 'No acumulable con otras promociones.' },
      { id: 'shoes', name: 'Paso Comfort', category: 'calzado', offer: '25% dto.', summary: 'Calzado profesional', description: 'Calzado profesional y ergonómico.', terms: 'Aplica en referencias seleccionadas.' },
      { id: 'institute', name: 'Instituto Biomédico', category: 'educacion', offer: '30% dto.', summary: 'Cursos y diplomados', description: 'Cursos cortos y diplomados.', terms: 'Cupos limitados.' },
      { id: 'editorial', name: 'Editorial Científica', category: 'educacion', offer: '10% dto.', summary: 'Libros y publicaciones', description: 'Material técnico y científico.', terms: 'Disponible en productos seleccionados.' },
      { id: 'coffee', name: 'Café Central', category: 'gastronomia', offer: '2×1', summary: 'Bebidas seleccionadas', description: 'Bebidas calientes todos los martes.', terms: 'Una promoción por asociado.' },
      { id: 'restaurant', name: 'Sabor Urbano', category: 'gastronomia', offer: '15% dto.', summary: 'Restaurante y café', description: 'Almuerzos ejecutivos.', terms: 'Válido de lunes a viernes.' },
      { id: 'optics', name: 'Óptica Prisma', category: 'bienestar', offer: '20% dto.', summary: 'Salud visual', description: 'Lentes, aros y examen visual.', terms: 'Algunas marcas están excluidas.' },
      { id: 'fitness', name: 'Vital Zen', category: 'bienestar', offer: 'Especial', summary: 'Actividad física', description: 'Planes de actividad física.', terms: 'Sujeto a disponibilidad.' },
      { id: 'insurance', name: 'Seguro Profesional Plus', category: 'servicios', offer: 'Especial', summary: 'Protección profesional', description: 'Responsabilidad civil profesional.', terms: 'Sujeto a evaluación.' },
      { id: 'technology', name: 'Tecno Pro', category: 'servicios', offer: '18% dto.', summary: 'Tecnología y soporte', description: 'Equipos y soporte seleccionado.', terms: 'Aplica sobre precio regular.' }
    ]
  },
  news: {
    unread: 3,
    items: [
      { id: 'benefits', category: 'INSTITUCIONAL', title: 'Nuevos beneficios para nuestros miembros', summary: 'Convenios en formación, salud y servicios profesionales.', publishedAt: 'Hace 2 horas', featured: true },
      { id: 'assembly', category: 'CONVOCATORIA', title: 'Asamblea General Ordinaria: documentos disponibles', summary: 'Consulta el orden del día y documentos de trabajo.', publishedAt: 'Hoy · 9:15 a. m.' },
      { id: 'agenda', category: 'EVENTOS', title: 'Conoce la agenda completa de la Jornada Nacional', summary: 'Revisa sesiones, horarios y conferencistas.', publishedAt: 'Ayer · 4:30 p. m.' }
    ]
  },
  certificates: {
    items: [
      { id: 'cert-event', title: 'Jornada Nacional 2026', detail: '12 horas · 20 junio 2026', available: true },
      { id: 'cert-quality', title: 'Seminario de Control de Calidad', detail: '6 horas · 15 marzo 2026', available: true }
    ]
  }
};

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export class AdministrativeSectionDisabledError extends Error {
  constructor(public section: AdministrativeSectionKey) {
    super(`Administrative section disabled: ${section}`);
    this.name = 'AdministrativeSectionDisabledError';
  }
}

export class AdministrativeSectionService {
  async loadSection<K extends AdministrativeSectionKey>(section: K): Promise<AdministrativeSectionDataMap[K]> {
    if (!isAdministrativeSectionEnabled(section)) {
      throw new AdministrativeSectionDisabledError(section);
    }

    const config = getAppConfig().administrative;
    if (config.useDummyData || !config.dataEndpoint) {
      return clone(dummySections[section]);
    }

    try {
      const endpoint = `${config.dataEndpoint.replace(/\/$/, '')}/${section}`;
      const response = await fetch(endpoint, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP_${response.status}`);
      return await response.json() as AdministrativeSectionDataMap[K];
    } catch (error) {
      console.warn(`AdministrativeSectionService: fallback dummy para ${section}.`, error);
      return clone(dummySections[section]);
    }
  }
}

export const administrativeSectionService = new AdministrativeSectionService();
